import {
  memo, useCallback, useId, useState,
} from 'react';
import styles from './CollapsibleCard.module.css';

/**
 * Reads a card's persisted open/closed state from localStorage, falling back to
 * `defaultOpen`. Exported so Analysis.js can seed its own gating state (e.g. the
 * valuation card's open flag, which decides whether the heavy regression hooks
 * run) from the SAME persisted value the card initializes with — so they agree
 * on the very first render, with no post-mount flash. Guarded against SSR /
 * unavailable localStorage / malformed values.
 */
export const readCardOpen = (storageKey, defaultOpen = false) => {
  if (!storageKey || typeof window === 'undefined' || !window.localStorage) return defaultOpen;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw == null) return defaultOpen;
    return raw === 'true';
  } catch {
    return defaultOpen;
  }
};

const writeCardOpen = (storageKey, open) => {
  if (!storageKey || typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(storageKey, open ? 'true' : 'false');
  } catch {
    /* localStorage unavailable (private mode / quota) — persistence is best-effort */
  }
};

/**
 * A card that collapses to a title-only header bar and LAZY-MOUNTS its body
 * (children are only rendered while open) — so a collapsed card's chart never
 * mounts and any expensive hooks feeding it can be skipped, cutting the
 * Analysis page's per-symbol render cost. Open/closed state persists per
 * `storageKey` in localStorage.
 *
 * The embedded cards (Ratio/Valuation/Sankey) render WITHOUT their own outer
 * `<section>`/`<h2>` (their `embedded` prop) since this component owns the card
 * chrome + title.
 *
 * @param {string} title - header label (also the region's accessible name)
 * @param {boolean} [defaultOpen=false] - initial state when nothing is persisted
 * @param {string} storageKey - localStorage key for persisted open/closed state
 * @param {(open: boolean) => void} [onToggle] - notified on every toggle, so a
 *   parent can gate work on this card's visibility (e.g. the valuation hooks)
 * @param {React.ReactNode} children - the card body; only mounted while open
 */
const CollapsibleCard = ({
  title, defaultOpen = false, storageKey, onToggle, children,
}) => {
  const [open, setOpen] = useState(() => readCardOpen(storageKey, defaultOpen));
  const regionId = useId();

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      writeCardOpen(storageKey, next);
      if (onToggle) onToggle(next);
      return next;
    });
  }, [storageKey, onToggle]);

  return (
    <section className={styles.card}>
      <button
        type="button"
        className={styles.header}
        aria-expanded={open}
        aria-controls={regionId}
        onClick={toggle}
      >
        <span className={styles.title}>{title}</span>
        <span
          className={`${styles.chevron}${open ? ` ${styles.chevronOpen}` : ''}`}
          aria-hidden="true"
        >
          &#8250;
        </span>
      </button>
      {open && (
        <div id={regionId} className={styles.body} role="region" aria-label={title}>
          {children}
        </div>
      )}
    </section>
  );
};

export default memo(CollapsibleCard);
